"use client";

import Image from "next/image";
import { useState, useId, useEffect, type FormEvent } from "react";
import { ERROR_BOX_TONE_CLASS, ERROR_DETAIL_TEXT_CLASS } from "@/components/ui/errorBoxClasses";
import { useTrackEvent } from "@/hooks/useTrackEvent";
import { useLanguage } from "@/context/LanguageContext";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { getAttributionSnapshot } from "@/lib/attribution";

type LeadMagnetProps = {
  eyebrow?: string;
  title: string;
  subtitle: string;
  ctaLabel: string;
  emailPlaceholder?: string;
  helperText?: string;
  reassuranceText?: string;
  consentText?: string;
  successMessage?: string;
  className?: string;
  context?: string;
  source?: string;
  leadType?: string;
  metadata?: Record<string, string>;
  trackWarmupRequest?: boolean;
  languageLabel?: string;
  image?: {
    src: string;
    alt: string;
    imagePosition?: string;
  };
  idBase?: string;
  onSuccess?: () => void;
  tone?: "default" | "premium";
};

export function LeadMagnet({
  eyebrow = "Download",
  title,
  subtitle,
  ctaLabel,
  emailPlaceholder = "you@email.com",
  helperText,
  reassuranceText,
  consentText =
    "Send me this free resource and occasional singing tips from Singing Attitude. I can unsubscribe or manage preferences at any time. Singing Attitude will process my details according to the Privacy Policy.",
  successMessage = "Sent. Check your inbox (and spam).",
  className,
  context = "general",
  source: sourceOverride,
  leadType,
  metadata,
  trackWarmupRequest = false,
  languageLabel = "Choose warmup language",
  image,
  idBase,
  onSuccess,
  tone = "default",
}: LeadMagnetProps) {
  const { language } = useLanguage();
  const [email, setEmail] = useState("");
  const [lang, setLang] = useState<"en" | "ru" | "ro">(
    language === "ru" || language === "ro" ? language : "en",
  );
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorRequestId, setErrorRequestId] = useState<string | null>(null);
  const fallbackId = useId();
  const baseId = (idBase ?? `leadmagnet-${fallbackId}`).replace(/[:]/g, "");
  const inputId = `${baseId}-email`;
  const languageSelectId = `${baseId}-language`;
  const trackEvent = useTrackEvent();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [consentPrefix, consentSuffix = "."] = consentText.split("Privacy Policy");
  const isPremium = tone === "premium";

  useEffect(() => {
    trackEvent("lead_magnet_open", { context });
  }, [context, trackEvent]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!emailRegex.test(email.trim())) {
      setErrorMessage("Enter a valid email address.");
      setErrorRequestId(null);
      setStatus("error");
      return;
    }
    setStatus("loading");
    setErrorMessage("");
    setErrorRequestId(null);
    try {
      const isDigestContext = context === "blog-digest" || context.startsWith("blog-article-");
      const source =
        sourceOverride ?? (isDigestContext ? "coaching_digest" : trackWarmupRequest ? "warmups_modal" : "lead_magnet");
      const response = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          // SA Admin lead views filter mailing-list entries by explicit consent=true.
          consent: true,
          name: null,
          lang,
          source,
          leadType: leadType ?? (isDigestContext ? "newsletter" : undefined),
          context,
          pagePath: window.location.pathname,
          metadata: {
            ...metadata,
            attribution: getAttributionSnapshot(),
          },
        }),
      });
      const data = (await response.json()) as { error?: string; requestId?: string };
      if (!response.ok) {
        setErrorRequestId(typeof data?.requestId === "string" ? data.requestId : null);
        throw new Error(data?.error || "Unable to send email.");
      }
      setStatus("success");
      setEmail("");
      onSuccess?.();
      trackEvent(ANALYTICS_EVENTS.leadCapture, { context, source });
      if (trackWarmupRequest) {
        trackEvent("warmup_download_request", { context });
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Something went wrong.");
      setStatus("error");
    }
  }

  return (
    <section
      className={`rounded-[28px] border p-6 shadow-soft backdrop-blur-md ${
        isPremium
          ? "border-[#e2d4bd] bg-[radial-gradient(circle_at_10%_0%,rgba(211,163,59,0.16),transparent_22rem),linear-gradient(135deg,rgba(255,253,248,0.98),rgba(255,250,242,0.9))] text-[#123b34] shadow-[0_24px_70px_rgba(49,39,24,0.09)] ring-1 ring-white/85"
          : "border-border-subtle bg-surface-glass/70"
      }${className ? ` ${className}` : ""}`}
    >
      <div className={`flex flex-col gap-6 ${image ? "lg:grid lg:grid-cols-[1.05fr_0.95fr] lg:items-center" : ""}`}>
        <div>
          <div className={`space-y-3 ${isPremium ? "text-[#123b34]" : "text-offwhite"}`}>
            <div className={`flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.3em] ${isPremium ? "text-[#a06c15]" : "text-emerald"}`}>
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${isPremium ? "border-[#d6aa42] bg-white/72 text-[#a06c15] shadow-[0_10px_24px_rgba(168,111,18,0.09)]" : "border-emerald/30 text-offwhite/80"}`}>
                {eyebrow}
              </span>
            </div>
            <h3 className={`${isPremium ? "max-w-2xl text-3xl sm:text-[2.35rem]" : "text-2xl"} font-serif font-semibold leading-tight tracking-tight`}>
              {title}
            </h3>
            <p className={`max-w-2xl text-sm leading-relaxed sm:text-base ${isPremium ? "text-[#5f665f]" : "text-offwhite/80"}`}>{subtitle}</p>
            {helperText ? <p className={`text-xs uppercase tracking-[0.18em] ${isPremium ? "text-[#6b746e]" : "text-offwhite/55"}`}>{helperText}</p> : null}
          </div>
          <form
            onSubmit={handleSubmit}
            className={
              isPremium
                ? "mt-6 grid gap-3 rounded-[22px] border border-[#eadfce] bg-white/72 p-3 shadow-[0_18px_48px_rgba(49,39,24,0.07)] ring-1 ring-white/80 backdrop-blur sm:grid-cols-[minmax(0,1fr)_11rem] sm:p-4 lg:grid-cols-[minmax(0,1fr)_10rem_auto] lg:items-end"
                : "mt-5 flex flex-col gap-3 sm:flex-row sm:items-end"
            }
          >
            <label htmlFor={inputId} className="sr-only">
              Email address
            </label>
            <input
              id={inputId}
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={emailPlaceholder}
              className={`w-full border px-4 py-2.5 text-sm focus:outline-none ${
                isPremium
                  ? "min-h-[3.45rem] rounded-[16px] border-[#dac8ad] bg-[#fffdf8] text-[#123b34] shadow-inner shadow-[#eadfce]/35 placeholder:text-[#8b928d] focus:border-[#c99a35] focus:ring-2 focus:ring-[#d6aa42]/20"
                  : "rounded-full border-border-subtle bg-surface-glass/70 text-offwhite placeholder:text-mist/70 focus:border-emerald"
              }`}
            />
            <div className={isPremium ? "sm:w-auto" : "sm:w-40"}>
              <label
                htmlFor={languageSelectId}
                className={`mb-1 block text-[10px] font-semibold uppercase tracking-[0.18em] ${isPremium ? "text-[#6b746e]" : "text-offwhite/55"}`}
              >
                {languageLabel}
              </label>
              <select
                id={languageSelectId}
                aria-label={languageLabel}
                value={lang}
                onChange={(event) => setLang(event.target.value as "en" | "ru" | "ro")}
                className={`w-full border px-4 py-2.5 text-sm focus:outline-none ${
                  isPremium
                    ? "min-h-[3.45rem] rounded-[16px] border-[#dac8ad] bg-[#fffdf8] text-[#123b34] shadow-inner shadow-[#eadfce]/35 focus:border-[#c99a35] focus:ring-2 focus:ring-[#d6aa42]/20"
                    : "rounded-full border-border-subtle bg-surface-glass/70 text-offwhite focus:border-emerald"
                }`}
              >
                <option value="en">EN</option>
                <option value="ru">RU</option>
                <option value="ro">RO</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={status === "loading"}
              className={`px-6 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed ${
                isPremium
                  ? "min-h-[3.45rem] rounded-[16px] bg-[linear-gradient(135deg,#e4bd55,#c99022)] text-[#112d28] shadow-[0_18px_38px_rgba(168,111,18,0.24)] ring-1 ring-[#f1d98b]/70 hover:-translate-y-0.5 hover:shadow-[0_22px_48px_rgba(168,111,18,0.28)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c99422]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white sm:col-span-2 lg:col-span-1"
                  : "btn-premium rounded-full bg-emerald text-accent-contrast hover:bg-emerald-soft"
              }`}
            >
              {status === "loading" ? "Sending..." : ctaLabel}
            </button>
          </form>
          <p className={`mt-3 text-xs leading-relaxed ${isPremium ? "text-[#6b746e]" : "text-offwhite/56"}`}>
            {consentPrefix}
            <a href="/privacy" className={`font-semibold ${isPremium ? "text-[#116255] hover:text-[#004f43]" : "text-emerald hover:text-emerald/80"}`}>
              Privacy Policy
            </a>
            {consentSuffix}
          </p>
          {reassuranceText ? <p className={`mt-2 text-xs font-medium ${isPremium ? "text-[#5f665f]" : "text-offwhite/70"}`}>{reassuranceText}</p> : null}
          {status === "success" && <p className={`mt-3 text-sm ${isPremium ? "text-[#116255]" : "text-emerald"}`}>{successMessage}</p>}
          {status === "error" && (
            <div className={`mt-3 rounded-xl border px-4 py-3 text-sm ${ERROR_BOX_TONE_CLASS}`}>
              <p>{errorMessage}</p>
              {errorRequestId ? <p className={`mt-1 ${ERROR_DETAIL_TEXT_CLASS}`}>Request ID: {errorRequestId}</p> : null}
            </div>
          )}
        </div>
        {image && (
          <div className={`relative aspect-[4/3] overflow-hidden rounded-2xl border ${isPremium ? "hidden border-[#eadfce] bg-[#fffaf2] lg:block" : "border-border bg-surface/60"}`}>
            <Image
              src={image.src}
              alt={image.alt}
              fill
              quality={92}
              className="object-cover"
              sizes="(min-width: 1024px) 36vw, 90vw"
              style={{ objectPosition: image.imagePosition ?? "50% 50%" }}
            />
          </div>
        )}
      </div>
    </section>
  );
}
